import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Search, RefreshCcw, Car, User } from "lucide-react";

type VeiculoRow = {
  id: string;
  placa?: string | null;
  marca?: string | null;
  modelo?: string | null;
  ano?: number | null;
  km?: number | null;
  createdAt?: string;

  cliente: {
    id: string;
    nome: string;
    documento?: string | null;
    telefone?: string | null;
  };
};

type Meta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  q: string;
};

export default function VeiculosList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const qParam = searchParams.get("q") ?? "";
  const pageParam = Number(searchParams.get("page") ?? "1");
  const limitParam = Number(searchParams.get("limit") ?? "20");

  const [q, setQ] = useState(qParam);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<VeiculoRow[]>([]);
  const [meta, setMeta] = useState<Meta>({
    page: pageParam,
    limit: limitParam,
    total: 0,
    totalPages: 1,
    q: qParam,
  });

  const canPrev = useMemo(() => meta.page > 1, [meta.page]);
  const canNext = useMemo(() => meta.page < meta.totalPages, [meta.page, meta.totalPages]);

  function updateUrl(next: { q?: string; page?: number; limit?: number }) {
    const newParams = new URLSearchParams(searchParams);

    if (next.q !== undefined) {
      if (next.q.trim()) newParams.set("q", next.q.trim());
      else newParams.delete("q");
    }

    if (next.page !== undefined) newParams.set("page", String(next.page));
    if (next.limit !== undefined) newParams.set("limit", String(next.limit));

    if (newParams.get("page") === "1") newParams.delete("page");
    if (newParams.get("limit") === "20") newParams.delete("limit");

    setSearchParams(newParams);
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/veiculos", {
        params: {
          q: qParam || undefined,
          page: pageParam || 1,
          limit: limitParam || 20,
        },
      });

      setRows(data?.data ?? []);
      setMeta(data?.meta ?? { page: 1, limit: 20, total: 0, totalPages: 1, q: "" });
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Erro ao carregar veículos";
      toast.error(msg);
      setRows([]);
      setMeta((m) => ({ ...m, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam, pageParam, limitParam]);

  function onSearch() {
    updateUrl({ q, page: 1 });
  }

  function formatVeiculo(v: VeiculoRow) {
    const marca = v.marca || "-";
    const modelo = v.modelo || "";
    return `${marca} ${modelo}`.trim();
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Car size={20} />
              Veículos
            </h1>
            <p className="text-sm text-slate-500">
              {meta.total ? `${meta.total} veículo(s) encontrado(s)` : "Todos os veículos cadastrados"}
            </p>
          </div>

          <Button variant="outline" onClick={load} className="gap-2" disabled={loading}>
            <RefreshCcw size={16} />
            Atualizar
          </Button>
        </div>

        {/* BUSCA */}
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg w-full">
              <Search size={16} className="text-slate-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSearch();
                }}
                placeholder="Buscar por placa, marca, modelo, nome do cliente..."
                className="bg-transparent outline-none text-sm w-full"
              />
            </div>

            <Button onClick={onSearch} disabled={loading} className="whitespace-nowrap">
              Buscar
            </Button>
          </div>

          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setQ("");
                updateUrl({ q: "", page: 1 });
              }}
              disabled={loading && rows.length === 0}
            >
              Limpar
            </Button>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Por página:</span>
              <select
                value={limitParam}
                onChange={(e) => updateUrl({ limit: Number(e.target.value), page: 1 })}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABELA */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3">Placa</th>
                  <th className="text-left px-4 py-3">Veículo</th>
                  <th className="text-left px-4 py-3">Ano</th>
                  <th className="text-left px-4 py-3">KM</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                      Carregando veículos...
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                      Nenhum veículo encontrado.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((v) => (
                    <tr key={v.id} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{v.placa || "-"}</td>
                      <td className="px-4 py-3">{formatVeiculo(v)}</td>
                      <td className="px-4 py-3">{v.ano ?? "-"}</td>
                      <td className="px-4 py-3">{v.km ?? "-"}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{v.cliente?.nome}</div>
                        <div className="text-xs text-slate-500">{v.cliente?.documento || ""}</div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Link to={`/clientes/${v.cliente.id}/editar`}>
                            <Button size="sm" variant="outline" className="gap-2">
                              <User size={16} />
                              Cliente
                            </Button>
                          </Link>

                          <Link to={`/clientes/${v.cliente.id}/veiculos`}>
                            <Button size="sm" variant="outline" className="gap-2">
                              <Car size={16} />
                              Ver
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* PAGINAÇÃO */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
            <p className="text-xs text-slate-500">
              Página <span className="font-medium text-slate-800">{meta.page}</span> de{" "}
              <span className="font-medium text-slate-800">{meta.totalPages}</span>
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => updateUrl({ page: meta.page - 1 })}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => updateUrl({ page: meta.page + 1 })}
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
